export interface GTMConfig {
  gtmId?: string;
  googleAnalyticsId?: string;
}

export interface UserData {
  isActive?: boolean;
  accountType?: string;
  team?: string;
  [key: string]: any;
}

export interface GTMEventData {
  event: string;
  [key: string]: any;
}

declare global {
  interface Window {
    dataLayer: GTMEventData[];
  }
}

export class GTMManager {
  private gtmId: string | null = null;
  private googleAnalyticsId: string | null = null;
  private initialized = false;
  private debug = process.env.NODE_ENV === 'development';
  private userData: UserData | null = null;
  private trackingParams: Record<string, string> = {};

  constructor(config?: GTMConfig) {
    if (config?.gtmId) {
      this.gtmId = config.gtmId;
    }
    if (config?.googleAnalyticsId) {
      this.googleAnalyticsId = config.googleAnalyticsId;
    }
  }

  // Validate GTM ID format
  private isValidGTMId(gtmId: string): boolean {
    return /^GTM-[A-Z0-9]{1,10}$/.test(gtmId);
  }

  // Initialize GTM - call this once on app startup
  initialize(): void {
    if (!this.gtmId || this.initialized || typeof window === 'undefined') {
      if (this.debug && !this.gtmId) {
        console.warn('GTM: No GTM ID provided, skipping initialization');
      }
      return;
    }

    if (!this.isValidGTMId(this.gtmId)) {
      console.error('GTM: Invalid GTM ID format:', this.gtmId);
      return;
    }

    try {
      // Add GTM script to head
      this.addGTMScript();

      // Add noscript fallback to body
      this.addGTMNoscript();

      this.initialized = true;

      if (this.debug) {
        console.log('GTM: Initialized with ID:', this.gtmId);
        if (this.googleAnalyticsId) {
          console.log('GTM: Google Analytics ID:', this.googleAnalyticsId);
        }
      }
    } catch (error) {
      console.error('GTM: Failed to initialize:', error);
    }
  }

  private addGTMScript(): void {
    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });

    // Add GTM script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${this.gtmId}`;

    // Add error handling
    script.onerror = () => {
      console.error('GTM: Failed to load GTM script');
    };

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  }

  private addGTMNoscript(): void {
    // Create noscript element with iframe
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');

    iframe.src = `https://www.googletagmanager.com/ns.html?id=${this.gtmId}`;
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    iframe.height = '0';
    iframe.width = '0';

    noscript.appendChild(iframe);

    // Insert after body opening tag
    if (document.body.firstChild) {
      document.body.insertBefore(noscript, document.body.firstChild);
    } else {
      document.body.appendChild(noscript);
    }
  }


  // Track page views
  trackPageView(data?: Record<string, any>): void {
    this.pushEvent({
      event: 'page_view',
      page_path: window.location.pathname,
      page_title: document.title,
      page_location: window.location.href,
      ...data
    });
  }

  // Track custom events
  trackEvent(eventName: string, parameters?: Record<string, any>): void {
    this.pushEvent({
      event: eventName,
      ...parameters
    });
  }

  // Track user interactions
  trackInteraction(action: string, parameters?: Record<string, any>): void {
    this.pushEvent({
      event: 'user_interaction',
      interaction_action: action,
      ...parameters
    });
  }

  // Track form submissions
  trackFormSubmission(formName: string, parameters?: Record<string, any>): void {
    this.pushEvent({
      event: 'form_submission',
      form_name: formName,
      ...parameters
    });
  }

  // Track downloads
  trackDownload(fileName: string, fileType: string, parameters?: Record<string, any>): void {
    this.pushEvent({
      event: 'file_download',
      file_name: fileName,
      file_type: fileType,
      ...parameters
    });
  }

  // Track video events
  trackVideoEvent(action: string, videoTitle: string, parameters?: Record<string, any>): void {
    this.pushEvent({
      event: 'video_interaction',
      video_action: action,
      video_title: videoTitle,
      ...parameters
    });
  }

  // Track errors
  trackError(errorMessage: string, errorStack?: string, parameters?: Record<string, any>): void {
    this.pushEvent({
      event: 'application_error',
      error_message: errorMessage,
      error_stack: errorStack,
      ...parameters
    });
  }

  // Track search
  trackSearch(searchTerm: string, resultsCount?: number, parameters?: Record<string, any>): void {
    this.pushEvent({
      event: 'search',
      search_term: searchTerm,
      results_count: resultsCount,
      ...parameters
    });
  }

  // Get initialization status
  isInitialized(): boolean {
    return this.initialized;
  }

  // Set user data for enhanced tracking
  setUserData(userData: UserData | null): void {
    this.userData = userData;

    if (this.initialized && userData) {
      // Push user data to dataLayer
      this.pushEvent({
        event: 'user_data_set',
        user_type: userData.isActive ? 'registered' : 'guest',
        account_type: userData.accountType,
      });
    }

    if (this.debug) {
      console.log('GTM: User data set:', userData);
    }
  }

  // Set tracking parameters (like _gl)
  setTrackingParams(params: Record<string, string>): void {
    this.trackingParams = { ...this.trackingParams, ...params };

    if (this.debug) {
      console.log('GTM: Tracking params set:', this.trackingParams);
    }
  }

  // Get tracking parameters
  getTrackingParams(): Record<string, string> {
    return this.trackingParams;
  }

  // Enhanced push event that includes user data
  pushEvent(eventData: GTMEventData): void {
    if (!this.initialized || typeof window === 'undefined') {
      if (this.debug && !this.initialized) {
        console.warn('GTM: Cannot push event - GTM not initialized', eventData);
      }
      return;
    }

    try {
      // Merge user data if available
      const enhancedData: GTMEventData = {
        ...eventData,
        ...this.trackingParams
      };

      if (this.userData) {
        enhancedData.user_type = this.userData.isActive ? 'registered' : 'guest';
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(enhancedData);

      if (this.debug) {
        console.log('GTM: Event pushed with user data:', enhancedData);
      }
    } catch (error) {
      console.error('GTM: Failed to push event:', error);
    }
  }
}

// Export singleton instance
export const gtmManager = new GTMManager();

// Helper functions for common tracking scenarios
export const trackPageView = (data?: Record<string, any>) => gtmManager.trackPageView(data);
export const trackEvent = (eventName: string, parameters?: Record<string, any>) => gtmManager.trackEvent(eventName, parameters);
export const trackInteraction = (action: string, parameters?: Record<string, any>) => gtmManager.trackInteraction(action, parameters);
export const trackFormSubmission = (formName: string, parameters?: Record<string, any>) => gtmManager.trackFormSubmission(formName, parameters);
export const trackDownload = (fileName: string, fileType: string, parameters?: Record<string, any>) => gtmManager.trackDownload(fileName, fileType, parameters);
export const trackVideoEvent = (action: string, videoTitle: string, parameters?: Record<string, any>) => gtmManager.trackVideoEvent(action, videoTitle, parameters);
export const trackError = (errorMessage: string, errorStack?: string, parameters?: Record<string, any>) => gtmManager.trackError(errorMessage, errorStack, parameters);
export const trackSearch = (searchTerm: string, resultsCount?: number, parameters?: Record<string, any>) => gtmManager.trackSearch(searchTerm, resultsCount, parameters);
export const trackNavigation = (path: string, parameters?: Record<string, any>) => gtmManager.trackEvent('navigation', { navigation_path: path, ...parameters });

// User data management
export const setUserData = (userData: UserData | null) => gtmManager.setUserData(userData);
export const setTrackingParams = (params: Record<string, string>) => gtmManager.setTrackingParams(params);
export const getTrackingParams = () => gtmManager.getTrackingParams();

// Initialize GTM with config
export const initializeGTM = (config: GTMConfig): void => {
  const newGtmManager = new GTMManager(config);
  // Replace the singleton with configured instance
  Object.setPrototypeOf(gtmManager, Object.getPrototypeOf(newGtmManager));
  Object.assign(gtmManager, newGtmManager);
  gtmManager.initialize();
};